import React from 'react';
import styled, { ColorsTypes } from 'styled-components';

import { Box, Flex, Icons, Typography } from 'components';
import { AnalysisReportItem, AnalysisReportStatusEnum, ReportsViews } from 'features/reports';
import { IconsTypes } from 'components/icons';
import { formatDate, stringfyArrayItems } from 'app/utils';

const ReportButton = styled.button`
  position: relative;
  border: 0;
  padding: 0 16px;
  text-align: left;
  outline: none;
`;

const ReportButtonLabel = styled.span`
  ${({ theme }) => theme.fontTypes.type3}
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-weight: 600;
`;

const ReportButtonIcon = styled(Icons)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  right: 15px;
`;

const CardSiding = styled(Flex)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.gray_600};
  z-index: 10;
  border-radius: 4px;
  display: none;
  padding: 0 15px;
  align-items: center;
`;

const CardIcon = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.gray_300};
`;

const CardWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.gray_300};
  border-radius: 4px;
  justify-content: space-between;
  position: relative;

  ${(props: any) => {
    if (props['data-view'] === 'grid') {
      return `
        flex-direction: column;
        width: 278px;
        min-height: 340px;

        ${ReportButton} {
          height: 50px;
          border-bottom-left-radius: 2px;
          border-bottom-right-radius: 2px;
        }
      `;
    }

    return `
      flex-direction: row;
      min-height: 60px;
      
      ${ReportButton} {
        
        flex-shrink: 0;
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;

        ${props.theme.mediaQueries.mobile} {
          width: 50px;

          ${ReportButtonLabel} {
            display: none;
          }
        }

        ${props.theme.mediaQueries.tablet} {
          width: 250px;

          ${ReportButtonLabel} {
            display: inline-block;
          }
        }
      }
    `;
  }}

  ${(props: any) => {
    if (props['data-status'] === AnalysisReportStatusEnum.Scheduled) {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.gray_500};
          background: ${props.theme.colors.gray_100};
          cursor: wait;
        }
        
        &:hover {
          ${ReportButton} {
            color: ${props.theme.colors.white};
            background: ${props.theme.colors.gray_500};
          }
        }
      `;
    }

    if (props['data-status'] === AnalysisReportStatusEnum.Available) {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.primary_400};
          background: ${props.theme.colors.gray_100};
          cursor: pointer;
        }

        &:hover {
          border-color: ${props.theme.colors.primary_400};

          ${ReportButton} {
              color: ${props.theme.colors.white};
              background: ${props.theme.colors.primary_400};
          }
        }

      `;
    }

    if (props['data-status'] === AnalysisReportStatusEnum.Unavailable) {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.primary_400};
          background: ${props.theme.colors.gray_100};
          cursor: not-allowed;
        }

        &:hover {
          ${CardSiding} {
            display: flex;
          }
        }

      `;
    }

    if (props['data-status'] === AnalysisReportStatusEnum.Expired) {
      return `
        ${ReportButton} {
          color: ${props.theme.colors.gray_500};
          background: ${props.theme.colors.gray_100};
          cursor: not-allowed;
        }
        
        &:hover {
          ${ReportButton} {
            color: ${props.theme.colors.white};
            background: ${props.theme.colors.gray_500};
          }
        }
      `;
    }
  }}
`;

interface ReportCardProps {
  view?: ReportsViews;
  report: AnalysisReportItem;
  downloadReport?: (uuid: string, fileName: string) => void;
}

const ReportCard = ({ view = 'grid', report, downloadReport }: ReportCardProps) => {
  let buttonIcon: IconsTypes = undefined!;
  let buttonLabel;
  let typeColor: ColorsTypes = undefined!;
  let analysisReportStatusFromLayout = report.analysisReportStatus;

  switch (report.analysisReportStatus) {
    case AnalysisReportStatusEnum.Scheduled:
      buttonIcon = 'calendarClock';
      buttonLabel = `${formatDate(report.availableAt).format('DD/MM/YYYY HH[h]mm')}`;
      typeColor = 'gray_500';
      break;

    case AnalysisReportStatusEnum.Available:
      buttonIcon = 'download';
      buttonLabel = `BAIXAR`;
      typeColor = 'dark_350';

      if (report.downloadedAt) {
        buttonLabel = `BAIXADO: ${formatDate(report.downloadedAt).format('DD/MM/YYYY')}`;
      }

      break;

    case AnalysisReportStatusEnum.Unavailable:
      buttonIcon = 'lock';
      buttonLabel = `INDISPONÍVEL`;
      typeColor = 'dark_350';
      analysisReportStatusFromLayout = !report.allowedCourses.length ? AnalysisReportStatusEnum.Expired : report.analysisReportStatus;
      break;

    case AnalysisReportStatusEnum.Expired:
      buttonLabel = `EXPIRADO`;
      typeColor = 'gray_500';
      buttonIcon = 'calendarRemove';
      break;

    default:
      break;
  }

  return (
    <CardWrapper data-view={view} data-status={analysisReportStatusFromLayout} flexWrap="nowrap">
      {report.analysisReportStatus === AnalysisReportStatusEnum.Unavailable && Boolean(report.allowedCourses.length) && (
        <CardSiding>
          <Flex textAlign="center" flexDirection="column">
            <Typography as="span" type="type2" color="white" fontWeight="bold" upper>
              Disponível para:
            </Typography>

            <Typography as="span" type="type1" color="white" lineHeight={1.3} mt={view === 'grid' ? 2 : 0}>
              {stringfyArrayItems(report.allowedCourses)}
            </Typography>
          </Flex>
        </CardSiding>
      )}

      <Flex flexGrow={1} flexDirection="column" justifyContent="space-between">
        <Flex flexGrow={0} justifyContent="space-between" flexWrap="nowrap">
          <Flex flexGrow={1} flexDirection={view === 'grid' ? 'column' : { m: 'column', t: 'row' }} justifyContent="space-between">
            <Flex
              flexGrow={0}
              flexDirection={view === 'grid' ? { m: 'row' } : { m: 'column', t: 'row' }}
              px={view === 'grid' ? { m: 3 } : { m: 2 }}
              pt={view === 'grid' ? { m: 3 } : { m: 2 }}
            >
              <Typography as="span" type={view === 'grid' ? 'type3' : 'type1'} fontWeight="bold" color={typeColor}>
                {report.analysisReportTypeName}
              </Typography>

              {view === 'list' && (
                <Typography as="span" type="type1" color="gray_500" ml={{ m: 0, t: 3 }} mt={{ m: 1, t: 0 }}>
                  {report.analysisReportProviderName}
                </Typography>
              )}
            </Flex>

            <Box flexGrow={0} px={view === 'grid' ? { m: 3 } : { m: 2, t: 3 }}>
              <Typography as="span" type="type0" color="gray_500">
                {formatDate(report.writtenAt).format('DD [de] MMMM [de] YYYY')}
              </Typography>
            </Box>
          </Flex>

          {view === 'grid' && (
            <Flex flexGrow={0} alignItems="start" pt={3} pr={3}>
              <CardIcon icon="fileDocumentOutline" />
            </Flex>
          )}
        </Flex>

        <Flex flexGrow={0} flexDirection="column" px={view === 'grid' ? { m: 3 } : { m: 2 }} pb={view === 'grid' ? { m: 3 } : { m: 2 }}>
          {view === 'grid' && (
            <Typography as="span" type="type1" color="gray_500" mb={1}>
              {report.analysisReportProviderName}
            </Typography>
          )}

          <Typography
            as="span"
            type="type5"
            color={typeColor}
            lineHeight={1.2}
            fontWeight="bold"
            mt={view === 'grid' ? { m: 0, t: 1 } : { m: 0, t: 1 }}
          >
            {report.name}
          </Typography>
        </Flex>
      </Flex>

      <ReportButton
        onClick={() => {
          if (report.analysisReportStatus === AnalysisReportStatusEnum.Available) {
            if (downloadReport) downloadReport(report.uuid, report.downloadFilename);
          }
        }}
      >
        <ReportButtonLabel>{buttonLabel}</ReportButtonLabel>
        {Boolean(buttonIcon) && <ReportButtonIcon icon={buttonIcon} />}
      </ReportButton>
    </CardWrapper>
  );
};

export default ReportCard;
